import {
  GenericFormField,
  CurrencyFieldExtraProps,
  AutoCompleteExtraProps,
  NumberFormatFieldExtraProps,
  GenericFormSection,
} from "models/propTypes/genericFormProps";
import { FormEnums } from "models/enums";
import { DateHelper, LocalizationHelperInstance, RegularExpressions, UtilsHelper, EnumHelper } from "utils/helpers";
import { TranslationKeys } from "translations";
import { UtilFormTypes } from "models/formTypes";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FieldConstants } from "utils/constants";
import { FormikErrors, FormikValues, getIn, setIn } from "formik";
import * as Yup from "yup";

const FormHelper = {
  getFormattedValue: <T>(value: any, values: any, field: GenericFormField<T>) => {
    let fieldValue = value;
    fieldValue = fieldValue !== undefined && fieldValue !== null ? fieldValue : "";

    switch (field.type) {
      case FormEnums.FormFieldType.CURRENCY:
        const currencyFieldProps = field.extraProps as CurrencyFieldExtraProps<T>;
        const currency =
          currencyFieldProps?.currency ??
          (currencyFieldProps?.currencyReferenceFieldName
            ? values[currencyFieldProps.currencyReferenceFieldName]
            : undefined);

        return LocalizationHelperInstance.getFormattedCurrency(+fieldValue, currency, 2, 2);

      case FormEnums.FormFieldType.CURRENCYRANGE:
        const currencyRangeFieldProps = field.extraProps as CurrencyFieldExtraProps<T>;
        const rangeCurrency =
          currencyRangeFieldProps?.currency ??
          (currencyRangeFieldProps?.currencyReferenceFieldName
            ? values[currencyRangeFieldProps.currencyReferenceFieldName]
            : undefined);
        const currencyRangeValue = fieldValue as UtilFormTypes.NumberRange;
        return `${
          currencyRangeValue.lowest !== undefined
            ? LocalizationHelperInstance.getFormattedCurrency(currencyRangeValue.lowest, rangeCurrency, 2, 2)
            : ""
        }${
          currencyRangeValue.highest !== undefined
            ? ` ${LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.Common.To
              ).toLowerCase()} ${LocalizationHelperInstance.getFormattedCurrency(
                currencyRangeValue.highest,
                rangeCurrency,
                2,
                2
              )}`
            : ""
        }`;

      case FormEnums.FormFieldType.NUMBER:
        const numberFormatFieldProps = field.extraProps as NumberFormatFieldExtraProps;
        const decimalPoints = numberFormatFieldProps?.decimalPoints ?? 0;
        return !numberFormatFieldProps?.hideValueIfReadOnly
          ? `${LocalizationHelperInstance.getFormattedNumber(+fieldValue, decimalPoints, decimalPoints)}${
              numberFormatFieldProps?.suffix ? ` ${numberFormatFieldProps.suffix}` : ""
            }`
          : "";
      case FormEnums.FormFieldType.NUMBERRANGE:
        const numberRangeFormatFieldProps = field.extraProps as NumberFormatFieldExtraProps;
        const rangeDecimalPoints = numberRangeFormatFieldProps?.decimalPoints ?? 0;
        const numberRangeValue = fieldValue as UtilFormTypes.NumberRange;
        return `${
          numberRangeValue.lowest !== undefined
            ? LocalizationHelperInstance.getFormattedNumber(
                numberRangeValue.lowest,
                rangeDecimalPoints,
                rangeDecimalPoints
              )
            : ""
        }${
          numberRangeValue.highest !== undefined
            ? ` ${LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.Common.To
              ).toLowerCase()} ${LocalizationHelperInstance.getFormattedNumber(
                numberRangeValue.highest,
                rangeDecimalPoints,
                rangeDecimalPoints
              )}`
            : ""
        }`;
      case FormEnums.FormFieldType.DATE:
        return fieldValue ? LocalizationHelperInstance.getFormattedDate(fieldValue) : "";
      case FormEnums.FormFieldType.TOGGLE:
        return fieldValue
          ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Yes)
          : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.No);
      case FormEnums.FormFieldType.AUTOCOMPLETE:
        const extraProps = field.extraProps as AutoCompleteExtraProps<T>;
        return extraProps && extraProps.valueSuffix ? `${fieldValue} ${extraProps.valueSuffix}` : fieldValue;
      case FormEnums.FormFieldType.MONTHRANGE:
        const monthRange = fieldValue as UtilFormTypes.DateRange;
        return monthRange
          ? DateHelper.formatMonthRange(monthRange.start ?? undefined, monthRange.end ?? undefined)
          : "";
      default:
        return fieldValue;
    }
  },
  getTableRowFieldInfo: (name: string) => {
    const fieldNameData = RegularExpressions.TableRowFieldNameRegex.exec(name);

    return fieldNameData
      ? {
          fieldName: fieldNameData[1],
          rowIndex: +fieldNameData[2],
          innerFieldName: fieldNameData[3],
        }
      : undefined;
  },
  formatReferenceFieldName: (currentFieldName: string, referenceFieldName: string) => {
    const tableRowData = RegularExpressions.TableRowFieldNameRegex.exec(currentFieldName);
    if (tableRowData !== null) return `${tableRowData[1]}[${tableRowData[2]}].${referenceFieldName}`;
    const periodIndex = currentFieldName.lastIndexOf(".");
    if (periodIndex !== -1) return `${currentFieldName.substring(0, periodIndex)}.${referenceFieldName}`;
    return referenceFieldName;
  },
  getBooleanSelectionItems: (): SelectionItem[] => {
    return [
      { id: 0, value: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.No) },
      { id: 1, value: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Yes) },
    ];
  },
  getItemListFromEnum: (enumType: any) => {
    return Object.keys(enumType)
      .filter((key) => !isNaN(Number(enumType[key as keyof typeof enumType])))
      .map((key, value, i) => {
        return { id: enumType[key], value: i[value] };
      });
  },
  getSectionNavKey: (section: GenericFormSection<any>) => {
    return section.navKey ?? (section.label ? `${section.label.replace(" ", "").replace(".", "").toLowerCase()}` : "");
  },

  isFormChanged: (initialValues: any, values: any, touchedFields: string[]) => {
    const isFormChanged = touchedFields.some(
      (Field) => !UtilsHelper.isValueEquals(initialValues[Field], values[Field])
    );
    return isFormChanged;
  },

  getTouchedObj: async (errors: any, touched: any): Promise<any> => {
    if (!errors || typeof errors !== "object") {
      return touched;
    }
    // Await sequentially so nested touched flags are set before returning (forEach(async) dropped them).
    // Only spread touched when its shape matches errors -- Formik may store boolean true, which can't spread.
    const allTouched: any = Array.isArray(errors)
      ? [...(Array.isArray(touched) ? touched : [])]
      : { ...(touched && typeof touched === "object" ? touched : {}) };
    for (const key of Object.keys(errors)) {
      const error = errors[key];
      const touchedVal = touched ? touched[key] : undefined;
      if (error && typeof error === "object") {
        allTouched[key] = await FormHelper.getTouchedObj(error, touchedVal);
      } else if (error) {
        allTouched[key] = true;
      }
    }
    return allTouched;
  },

  getErrorMessages: (errors: any): string[] => {
    if (typeof errors === "string") {
      return [errors];
    }
    if (!errors || typeof errors !== "object") {
      return [];
    }
    return Object.keys(errors).flatMap((key) => FormHelper.getErrorMessages(errors[key]));
  },

  prepareDataForValidation: <T extends FormikValues>(values: T): FormikValues => {
    const data: FormikValues = Array.isArray(values) ? [] : {};
    for (const k in values) {
      if (Object.prototype.hasOwnProperty.call(values, k)) {
        const key = String(k);
        if (Array.isArray(values[key]) === true) {
          data[key] = values[key].map((value: any) => {
            if (Array.isArray(value) === true || UtilsHelper.isPlainObject(value)) {
              return FormHelper.prepareDataForValidation(value);
            } else {
              return value !== "" ? value : undefined;
            }
          });
        } else if (UtilsHelper.isPlainObject(values[key])) {
          data[key] = FormHelper.prepareDataForValidation(values[key]);
        } else {
          data[key] = values[key] !== "" ? values[key] : undefined;
        }
      }
    }
    return data;
  },

  validateYupSchema: <T extends FormikValues>(
    values: T,
    schema: Yup.Schema<T>,
    context: any = {}
  ): Promise<Partial<T>> => {
    const validateData: FormikValues = FormHelper.prepareDataForValidation(values);
    return schema.validate(validateData, {
      abortEarly: false,
      context: context,
    });
  },

  yupToFormErrors: <Values>(yupError: any): FormikErrors<Values> => {
    let errors: FormikErrors<Values> = {};
    if (yupError.inner) {
      if (yupError.inner.length === 0) {
        return setIn(errors, yupError.path, yupError.message);
      }
      for (const err of yupError.inner) {
        if (!getIn(errors, err.path)) {
          errors = setIn(errors, err.path, err.message);
        }
      }
    }
    return errors;
  },

  getDynamicFormFieldName: (id: string) => `${id}${FieldConstants.DynamicFieldSuffix}`,

  getAgentSaleInvoiceItems: (): SelectionItem[] => {
    return EnumHelper.getNumericItemValues(WintSuperkollAPIModel.DownPaymentVisibility).map((keyValue) => ({
      id: keyValue as number,
      value: LocalizationHelperInstance.getFormattedMessage(`Common.DownPaymentVisibility_${keyValue}`),
    }));
  },

  evaluateHideOptionalIndicator: <T>(
    hideOptionalIndicator: boolean | ((values: T) => boolean) | undefined,
    values: T
  ): boolean | undefined => {
    return typeof hideOptionalIndicator === "function" ? hideOptionalIndicator(values) : hideOptionalIndicator;
  },
};

export default FormHelper;
